/**
 * GovernanceGateDisplay — reusable witness-before-effect display surfaces.
 *
 * These components carry no PDBG-specific domain model. They render generic
 * gate checks, witness records, and typed rejection receipts for any demo that
 * has an admit-or-reject governance gate.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import { FONT, MODE_COLOR, THEOREM_COLOR } from "../design-system/tokens.js";

export interface GovernanceGateTheme {
  readonly surface: string;
  readonly elevated: string;
  readonly border: string;
  readonly text: string;
  readonly bright: string;
  readonly muted: string;
  readonly success: string;
  readonly primary: string;
  readonly danger: string;
  readonly mono: string;
  readonly body: string;
}

export const DEFAULT_GOVERNANCE_GATE_THEME: GovernanceGateTheme = {
  surface: "rgba(17,24,39,0.76)",
  elevated: "rgba(10,14,23,0.72)",
  border: "rgba(42,52,68,0.92)",
  text: "#c8d4e0",
  bright: "#e8f0f8",
  muted: "#6b7a8d",
  success: MODE_COLOR.immune.primary,
  primary: THEOREM_COLOR.PRECOMMIT,
  danger: "#cc3311",
  mono: FONT.mono,
  body: FONT.body,
};

export interface GovernanceGatePillProps {
  readonly label: string;
  readonly color: string;
  readonly title?: string;
  readonly theme?: GovernanceGateTheme;
}

export function GovernanceGatePill({
  label,
  color,
  title,
  theme = DEFAULT_GOVERNANCE_GATE_THEME,
}: GovernanceGatePillProps): React.JSX.Element {
  return (
    <span
      title={title}
      style={{
        alignItems: "center",
        background: `${color}18`,
        border: `1px solid ${color}66`,
        borderRadius: 999,
        color,
        display: "inline-flex",
        fontFamily: theme.mono,
        fontSize: 11,
        fontWeight: 800,
        gap: 6,
        padding: "5px 9px",
      }}
    >
      {label}
    </span>
  );
}

export interface GovernanceGateSurfaceProps {
  readonly children: React.ReactNode;
  readonly glow?: string;
  readonly compact?: boolean;
  readonly theme?: GovernanceGateTheme;
}

export function GovernanceGateSurface({
  children,
  glow,
  compact = false,
  theme = DEFAULT_GOVERNANCE_GATE_THEME,
}: GovernanceGateSurfaceProps): React.JSX.Element {
  return (
    <div
      style={{
        background:
          "linear-gradient(145deg, rgba(255,255,255,0.055), transparent 42%), " + theme.surface,
        border: `1px solid ${theme.border}`,
        borderRadius: 12,
        boxShadow: `0 18px 44px rgba(0,0,0,0.32)${glow === undefined ? "" : `, 0 0 0 1px ${glow}`}`,
        padding: compact ? 14 : 18,
      }}
    >
      {children}
    </div>
  );
}

export interface GovernanceGateField {
  readonly label: string;
  readonly value: string;
}

export interface GovernanceGatePredicate {
  readonly id: string;
  readonly description: string;
  readonly outcomeLabel: string;
  readonly color: string;
}

export interface GovernanceGateWitnessRecordProps {
  readonly title: string;
  readonly subtitle: string;
  readonly decisionLabel: string;
  readonly decisionColor: string;
  readonly rejected?: boolean;
  readonly fields: readonly GovernanceGateField[];
  readonly predicates: readonly GovernanceGatePredicate[];
  readonly theme?: GovernanceGateTheme;
}

export function GovernanceGateWitnessRecord({
  title,
  subtitle,
  decisionLabel,
  decisionColor,
  rejected = false,
  fields,
  predicates,
  theme = DEFAULT_GOVERNANCE_GATE_THEME,
}: GovernanceGateWitnessRecordProps): React.JSX.Element {
  return (
    <div
      aria-label={title}
      className="rounded-xl border"
      style={{
        background:
          "linear-gradient(145deg, rgba(255,255,255,0.045), transparent 38%), " + theme.elevated,
        borderColor: rejected ? `${theme.danger}66` : theme.border,
        boxShadow: rejected ? `0 0 0 1px ${theme.danger}22` : "none",
      }}
    >
      <header
        className="flex flex-wrap items-start justify-between gap-3 border-b px-4 py-3"
        style={{ borderColor: theme.border }}
      >
        <div>
          <h3 className="m-0 text-[16px] font-extrabold" style={{ color: theme.bright }}>
            {title}
          </h3>
          <p className="m-0 mt-1 text-[11.5px]" style={{ color: theme.muted }}>
            {subtitle}
          </p>
        </div>
        <GovernanceGatePill label={decisionLabel} color={decisionColor} theme={theme} />
      </header>

      <div className="grid gap-2 px-4 py-3">
        {fields.map((field) => (
          <GovernanceGateFieldRow key={field.label} field={field} theme={theme} />
        ))}
      </div>

      <div className="px-4 pb-4">
        <div
          className="mb-2 text-[11px] font-extrabold uppercase tracking-[0.08em]"
          style={{ color: theme.muted, fontFamily: theme.mono }}
        >
          Predicates
        </div>
        <div className="grid gap-2">
          {predicates.map((predicate) => (
            <div
              key={predicate.id}
              className="flex flex-wrap items-center gap-2 rounded-lg border px-3 py-2"
              style={{
                background: `${predicate.color}10`,
                borderColor: `${predicate.color}4D`,
              }}
            >
              <GovernanceGatePill
                label={predicate.outcomeLabel}
                color={predicate.color}
                theme={theme}
              />
              <span className="min-w-0 text-[12.5px] leading-[1.5]" style={{ color: theme.text }}>
                <strong style={{ color: theme.bright, fontFamily: theme.mono }}>
                  {predicate.id}
                </strong>{" "}
                · {predicate.description}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function GovernanceGateFieldRow({
  field,
  theme,
}: {
  readonly field: GovernanceGateField;
  readonly theme: GovernanceGateTheme;
}): React.JSX.Element {
  return (
    <div
      className="grid gap-1 rounded-lg border px-3 py-2 sm:grid-cols-[120px_minmax(0,1fr)]"
      style={{ background: theme.elevated, borderColor: theme.border }}
    >
      <span
        style={{
          color: theme.muted,
          fontFamily: theme.mono,
          fontSize: 11,
          fontWeight: 800,
          textTransform: "uppercase",
        }}
      >
        {field.label}
      </span>
      <span
        className="break-words"
        style={{ color: theme.bright, fontFamily: theme.mono, fontSize: 12 }}
      >
        {field.value}
      </span>
    </div>
  );
}

export interface GovernanceGateReceiptProps {
  readonly receiptId: string;
  readonly reason: string;
  readonly createdAt: string;
  readonly violatedPredicate: string | null;
  readonly remediation?: string;
  readonly missingItems?: readonly string[];
  readonly missingItemsLabel?: string;
  readonly gradeLabel?: string;
  readonly gradeColor?: string;
  readonly theme?: GovernanceGateTheme;
}

export function GovernanceGateReceipt({
  receiptId,
  reason,
  createdAt,
  violatedPredicate,
  remediation,
  missingItems,
  missingItemsLabel = "Missing items",
  gradeLabel,
  gradeColor,
  theme = DEFAULT_GOVERNANCE_GATE_THEME,
}: GovernanceGateReceiptProps): React.JSX.Element {
  return (
    <aside
      aria-label={`Decision receipt ${receiptId}`}
      role="status"
      className="rounded-xl border px-4 py-3"
      style={{
        background:
          `linear-gradient(145deg, ${theme.danger}29, transparent 46%), ` + theme.elevated,
        borderColor: `${theme.danger}66`,
        boxShadow: `0 18px 44px rgba(0,0,0,0.32), 0 0 0 1px ${theme.danger}22`,
      }}
    >
      <header className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <GovernanceGatePill label="REJECTED" color={theme.danger} theme={theme} />
          <code style={{ color: theme.danger, fontFamily: theme.mono, fontSize: 12 }}>
            {receiptId}
          </code>
        </div>
        <time
          dateTime={createdAt}
          style={{ color: theme.muted, fontFamily: theme.mono, fontSize: 11 }}
        >
          {createdAt}
        </time>
      </header>
      <p className="m-0 mt-3 text-[13px] leading-[1.65]" style={{ color: theme.bright }}>
        {reason}
      </p>
      {violatedPredicate !== null && (
        <p className="m-0 mt-2 text-[12px] leading-[1.6]" style={{ color: theme.text }}>
          First-failing predicate:{" "}
          <code style={{ color: theme.bright, fontFamily: theme.mono }}>{violatedPredicate}</code>
        </p>
      )}
      {missingItems !== undefined && missingItems.length > 0 && (
        <p className="m-0 mt-2 text-[12px] leading-[1.6]" style={{ color: theme.text }}>
          {missingItemsLabel}:{" "}
          <code style={{ color: theme.bright, fontFamily: theme.mono }}>
            {missingItems.join(", ")}
          </code>
        </p>
      )}
      {remediation !== undefined && (
        <div
          className="mt-3 rounded-lg border px-3 py-2 text-[12.5px] leading-[1.6]"
          style={{
            background: theme.elevated,
            borderColor: theme.border,
            color: theme.text,
          }}
        >
          <strong style={{ color: theme.bright }}>Remediation:</strong> {remediation}
        </div>
      )}
      {gradeLabel !== undefined && gradeColor !== undefined && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <span style={{ color: theme.muted, fontSize: 12 }}>Replayability</span>
          <GovernanceGatePill label={gradeLabel} color={gradeColor} theme={theme} />
        </div>
      )}
    </aside>
  );
}

export interface GovernanceGateCheckCardProps {
  readonly index: number;
  readonly title: string;
  readonly detail: string;
  readonly statusLabel: string;
  readonly statusColor: string;
  readonly active?: boolean;
  readonly theme?: GovernanceGateTheme;
}

export function GovernanceGateCheckCard({
  index,
  title,
  detail,
  statusLabel,
  statusColor,
  active = false,
  theme = DEFAULT_GOVERNANCE_GATE_THEME,
}: GovernanceGateCheckCardProps): React.JSX.Element {
  return (
    <div
      style={{
        background: active ? `${statusColor}18` : theme.elevated,
        border: `1px solid ${active ? `${statusColor}88` : theme.border}`,
        borderRadius: 10,
        display: "grid",
        gap: 6,
        minHeight: 118,
        padding: 14,
      }}
    >
      <div
        style={{
          alignItems: "center",
          color: statusColor,
          display: "flex",
          fontFamily: theme.mono,
          fontSize: 11,
          fontWeight: 800,
          gap: 8,
          textTransform: "uppercase",
        }}
      >
        <span
          style={{
            alignItems: "center",
            border: `1px solid ${statusColor}`,
            borderRadius: 999,
            display: "inline-flex",
            height: 22,
            justifyContent: "center",
            width: 22,
          }}
        >
          {index}
        </span>
        {statusLabel}
      </div>
      <strong style={{ color: theme.bright, fontSize: 14 }}>{title}</strong>
      <span style={{ color: theme.text, fontSize: 12.5, lineHeight: 1.55 }}>{detail}</span>
    </div>
  );
}
